// Turns a pasted backlog into tips to add. One tip per line:
//
//   username, Spanks, promised last show
//   username, 50
//   username  Song request  wants the birthday one
//
// Fields are split on commas or tabs, or on two-plus spaces. The second
// field is a menu item name (matched case-insensitively), a token amount,
// or an amount followed by a label for something not on the menu
// ("777 Song request"); anything after it is the note. Blank lines are
// skipped.

export type MenuPick = { name: string; amount: number };

export type ParsedTip = {
  line: number;
  raw: string;
  username: string;
  tokens: number;
  itemName: string | null;
  message: string;
  error: string | null;
};

const splitFields = (line: string) =>
  line
    .split(/\t|,|\s{2,}/)
    .map((f) => f.trim())
    .filter((f, i, all) => f.length > 0 || i < all.length - 1);

export const parseManualTips = (
  text: string,
  menu: MenuPick[],
): ParsedTip[] => {
  const byName = new Map(menu.map((m) => [m.name.toLowerCase(), m]));
  return text
    .split(/\r?\n/)
    .map((raw, i) => ({ raw, line: i + 1 }))
    .filter(({ raw }) => raw.trim().length > 0)
    .map(({ raw, line }) => {
      const [username = "", what = "", ...rest] = splitFields(raw);
      const message = rest.join(", ").trim();
      const base = { line, raw, username, message, itemName: null as string | null, tokens: 0 };
      if (!username) {
        return { ...base, error: "No username" };
      }
      if (!what) {
        return { ...base, error: "No item or amount" };
      }
      const item = byName.get(what.toLowerCase());
      if (item) {
        return { ...base, tokens: item.amount, itemName: null, error: null };
      }
      const tokens = Number(what.replace(/[^\d]/g, ""));
      if (/^\d[\d,]*$/.test(what) && Number.isInteger(tokens) && tokens > 0) {
        return { ...base, tokens, itemName: null, error: null };
      }
      const custom = what.match(/^(\d[\d,]*)\s+(.+)$/);
      if (custom && Number(custom[1].replace(/,/g, "")) > 0) {
        return {
          ...base,
          tokens: Number(custom[1].replace(/,/g, "")),
          itemName: custom[2].trim(),
          error: null,
        };
      }
      return { ...base, error: `"${what}" isn't a menu item or a token amount` };
    });
};
